import { Fragment } from 'react'
import type { MetricKey } from '@/lib/financeGlossary'
import type { PnLReport } from '@/types'
import { cn } from '@/lib/utils'
import { MONEY_SERIES, TermLabel, useMoney } from './moneyUi'
import { buildBridge, shareOf, type BridgeStep } from './pnlStatementLogic'

/**
 * The P&L headline (F-FE-N203): the period's money as one sum read left to
 * right — Sales − Cost of goods − Operating expenses = Profit — over a bar of
 * where each rupee of sales went. Big picture only; the statement below has the
 * detail.
 */

const STEP: Record<BridgeStep['key'], { label: string; metric?: MetricKey; colour: string }> = {
  sales: { label: 'Sales (after tax)', metric: 'net_sales', colour: MONEY_SERIES.sales },
  cogs: { label: 'Cost of goods', metric: 'cogs', colour: MONEY_SERIES.cogs },
  opex: { label: 'Operating expenses', metric: 'operating_expenses', colour: MONEY_SERIES.opex },
  refunds: { label: 'Refunds', metric: 'refunds_issued', colour: MONEY_SERIES.neutral },
  profit: { label: 'Profit', metric: 'net_profit', colour: MONEY_SERIES.sales },
}

export function PnlBridge({ summary, onOpenLedger }: { summary: PnLReport['summary']; onOpenLedger?: () => void }) {
  const money = useMoney()
  const bridge = buildBridge(summary)
  const { sales, profit, shares } = bridge
  const loss = profit < -0.005
  const hasSales = sales > 0.005

  const sub = (s: BridgeStep): string => {
    if (s.key === 'sales') return summary.total_orders > 0 ? `${summary.total_orders} orders` : 'No completed orders'
    if (!hasSales) return s.key === 'profit' ? (loss ? 'A loss, with no sales' : '—') : 'No sales this period'
    if (s.key === 'profit') return loss ? `${shareOf(-profit, sales)} of sales lost` : `${shareOf(profit, sales)} of sales kept`
    return `${shareOf(s.amount, sales)} of sales`
  }

  const operator = (s: BridgeStep) => (s.key === 'profit' ? '=' : '−')

  return (
    <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
      <div className="grid grid-cols-2 gap-px bg-border lg:flex lg:gap-0 lg:bg-card">
        {bridge.steps.map((s, i) => {
          const meta = STEP[s.key]
          const isProfit = s.key === 'profit'
          const clickable = s.key === 'opex' && onOpenLedger
          const cell = (
            <>
              <div className="flex items-center gap-2">
                <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ background: isProfit && loss ? MONEY_SERIES.loss : meta.colour }} />
                <TermLabel label={meta.label} metric={meta.metric} className="text-sm text-muted-foreground" />
              </div>
              <p
                className={cn(
                  'mt-2 tracking-tight',
                  isProfit ? 'text-3xl font-bold' : 'text-2xl font-semibold',
                  isProfit && loss ? 'text-danger-fg' : 'text-foreground',
                )}
                title={money.full(s.amount)}
              >
                {money.whole(s.amount)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">{sub(s)}</p>
            </>
          )
          return (
            <Fragment key={s.key}>
              {i > 0 && (
                <span aria-hidden className="hidden select-none items-center px-1 text-xl font-light text-muted-foreground/60 lg:flex">
                  {operator(s)}
                </span>
              )}
              {clickable ? (
                <button
                  type="button"
                  onClick={onOpenLedger}
                  aria-label={`${meta.label}: ${money.whole(s.amount)}. Open the ledger`}
                  className="min-w-0 flex-1 bg-card p-5 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  {cell}
                </button>
              ) : (
                <div className={cn('min-w-0 flex-1 bg-card p-5', isProfit && 'lg:bg-muted/30')}>{cell}</div>
              )}
            </Fragment>
          )
        })}
      </div>

      {hasSales && (
        <div className="border-t px-5 py-4">
          <p className="mb-2 text-xs text-muted-foreground">
            {loss
              ? `Costs ran ${money.whole(-profit)} past sales`
              : `Of every ${money.symbol} 100 of sales, ${money.symbol} ${Math.round(shares.profit ?? 0)} was kept`}
          </p>
          <BridgeBar bridge={bridge} />
        </div>
      )}
    </div>
  )
}

function BridgeBar({ bridge }: { bridge: ReturnType<typeof buildBridge> }) {
  const { steps, shares, overrunShare } = bridge
  // On a loss the bar is the costs, scaled so sales sits at a marked line.
  const span = 100 + overrunShare
  const slices = steps.filter(s => s.key !== 'sales' && (shares[s.key] ?? 0) > 0)
  return (
    <div className="relative">
      <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted" role="img" aria-label={barLabel(bridge)}>
        {slices.map(s => (
          <div
            key={s.key}
            style={{ width: `${((shares[s.key] ?? 0) / span) * 100}%`, background: STEP[s.key].colour }}
            title={`${STEP[s.key].label} ${(shares[s.key] ?? 0).toFixed(1)}%`}
          />
        ))}
      </div>
      {overrunShare > 0 && (
        <div
          aria-hidden
          className="absolute -top-1 h-5 w-0.5 rounded bg-foreground"
          style={{ left: `${(100 / span) * 100}%` }}
          title="Sales"
        />
      )}
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {slices.map(s => (
          <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
            <span aria-hidden className="h-2 w-2 rounded-[2px]" style={{ background: STEP[s.key].colour }} />
            {STEP[s.key].label} {(shares[s.key] ?? 0).toFixed(1)}%
          </span>
        ))}
      </div>
    </div>
  )
}

function barLabel({ steps, shares }: ReturnType<typeof buildBridge>): string {
  return steps
    .filter(s => s.key !== 'sales')
    .map(s => `${STEP[s.key].label} ${(shares[s.key] ?? 0).toFixed(1)}% of sales`)
    .join(', ')
}
